/* store.js — tiny reactive core for the webchat UI.
 *
 * Zero dependencies, no build step. The spine of the "liveness" redesign: one
 * source-of-truth signal per concept, every consumer subscribes, nobody
 * hand-calls sibling renderers. Mutations notify subscribers automatically, so
 * the UI can no longer silently go stale.
 *
 * Primitives:
 *   signal(initial, {equals})  observable atom: get/peek/set/update/subscribe
 *   computed(fn)               derived, auto-tracked, cached signal
 *   effect(fn)                 re-runs when any signal read inside it changes
 *   bind(elOrId, sig, render)  declarative DOM ↔ signal glue (browser only)
 *
 * Notifications are batched into one microtask, so a burst of .set() calls
 * collapses into a single re-render. Dependencies are tracked automatically —
 * consumers never maintain subscription lists that rot.
 */

// --- scheduler: batch notifications into one microtask -----------------------
let _queue = new Set();
let _flushing = false;
let _activeEffect = null; // dependency-tracking context (the effect currently running)

function _schedule(sub) {
  _queue.add(sub);
  if (_flushing) return;
  _flushing = true;
  queueMicrotask(_flush);
}

function _flush() {
  // Snapshot the queue; effects scheduled *during* the flush land in a fresh
  // queue and trigger another microtask, so chained derivations converge.
  const run = _queue;
  _queue = new Set();
  _flushing = false;
  for (const sub of run) {
    try {
      sub._run();
    } catch (e) {
      console.error("[store] effect error", e);
    }
  }
}

// --- signal: an observable atom ----------------------------------------------
export function signal(initial, { equals = Object.is } = {}) {
  let value = initial;
  const subs = new Set(); // effects/computeds depending on this signal

  const s = {
    get() {
      if (_activeEffect) {
        subs.add(_activeEffect);
        _activeEffect._deps.add(subs);
      }
      return value;
    },
    peek() {
      return value; // read without subscribing
    },
    set(next) {
      const v = typeof next === "function" ? next(value) : next;
      if (equals(v, value)) return; // no-op on unchanged value
      value = v;
      for (const sub of subs) _schedule(sub);
    },
    update(fn) {
      s.set(fn(value));
    },
    subscribe(fn) {
      // Fires immediately with the current value, then on every change.
      // Returns an unsubscribe function.
      return effect(() => {
        fn(s.get());
      });
    },
    _subs: subs,
  };
  return s;
}

// --- effect: re-runs when any signal it read changes -------------------------
export function effect(fn) {
  const runner = {
    _deps: new Set(),
    _cleanupFn: null,
    _run() {
      _cleanup(runner);
      const prev = _activeEffect;
      _activeEffect = runner;
      try {
        const ret = fn();
        if (typeof ret === "function") runner._cleanupFn = ret;
      } finally {
        _activeEffect = prev;
      }
    },
  };
  runner._run();
  return () => _cleanup(runner); // disposer
}

function _cleanup(runner) {
  for (const subs of runner._deps) subs.delete(runner);
  runner._deps.clear();
  if (typeof runner._cleanupFn === "function") {
    runner._cleanupFn();
    runner._cleanupFn = null;
  }
}

// --- computed: a derived, cached signal --------------------------------------
export function computed(fn, opts) {
  const out = signal(undefined, opts);
  effect(() => out.set(fn())); // auto-tracks deps read inside fn()
  return {
    get: out.get,
    peek: out.peek,
    subscribe: out.subscribe,
    _subs: out._subs,
  };
}

// --- bind: declarative DOM ↔ signal glue -------------------------------------
//   bind(elOrId, signal, renderFn) — renderFn(value, el) runs on every change.
//   Skips safely when the element is missing or detached (panel not mounted).
//   Returns a disposer.
export function bind(elOrId, sig, renderFn) {
  const resolve = () =>
    typeof elOrId === "string" ? document.getElementById(elOrId) : elOrId;
  return effect(() => {
    const v = sig.get(); // track
    const el = resolve();
    if (!el || !el.isConnected) return; // not mounted yet — skip; re-runs on next change
    renderFn(v, el);
  });
}
